(function () {
  'use strict';

  var ROOT_CLASS = 'custom-cursor-enabled';

  if (!document.documentElement.classList.contains(ROOT_CLASS)) return;

  var fineQuery = window.matchMedia('(any-pointer: fine)');
  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!fineQuery.matches || motionQuery.matches) return;

  var HOVER_SELECTOR = 'a, button, input, textarea, select, label,[role="button"], [role="link"],' +
    '.accordion__summary, .image-magnify-hover, .pvr-slide__media, .image-with-hotspots__dot,' +
    '.price-range__thumbs, .stories__products-title, .button--modal-opener,' +
    '.button, .btn, .tab-js, .slider-scrollbar__track, .slider-scrollbar__thumb, [data-cursor-hover]';

  // Interactive elements that should NOT trigger the ring scale.
  // Mark any element (or its ancestor) with data-cursor-no-hover / .no-cursor-hover.
  var HOVER_EXCLUDE = '.disabled, [disabled], .pickup-availability--unavailable,' +
    '[data-cursor-no-hover], .no-cursor-hover';

  // Zones where the custom cursor is fully suppressed and the native pointer
  // returns (embeds, video, maps, 3D viewers). Extend via data-cursor-ignore.
  var IGNORE_SELECTOR = '.ignore-custom-cursor, [data-cursor-ignore], iframe, embed, object, video';

  var DOT_EASE = 0.35;
  var RING_EASE = 0.18;
  var SCALE_EASE = 0.2;
  var RING_HOVER_SCALE = 1.6;
  var EPSILON = 0.1;

  // Pencil trail: a thin, slightly rough stroke drawn on a canvas that
  // fades out with age.
  var TRAIL_LIFE = 700;        // ms a point stays on screen
  var TRAIL_MIN_DIST = 1.5;    // px of travel before a new point is added
  var TRAIL_WIDTH = 1.6;
  var TRAIL_ALPHA = 0.75;

  var trailEl = null;          // <canvas>
  var trailCtx = null;
  var trailPts = [];           // { x, y, t, j } newest last
  var trailDpr = 1;
  var trailColor = '#fff';

  var dotEl = null;
  var ringEl = null;

  var pointer = { x: 0, y: 0 };
  var dot = { x: 0, y: 0, halfW: 6, halfH: 6 };
  var ring = { x: 0, y: 0, halfW: 36, halfH: 36, scale: 1, targetScale: 1 };

  var rafId = null;
  var running = false;
  var visible = false;
  var seenPointer = false;
  var ignored = false;
  var destroyed = false;
  var lastTarget = null;   // skip closest() work while the pointer stays on one node

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function sizeTrail() {
    if (!trailEl) return;
    trailDpr = Math.min(window.devicePixelRatio || 1, 2);
    trailEl.width = Math.round(window.innerWidth * trailDpr);
    trailEl.height = Math.round(window.innerHeight * trailDpr);
    trailCtx = trailEl.getContext('2d');
    trailCtx.setTransform(trailDpr, 0, 0, trailDpr, 0, 0);
    trailCtx.lineCap = 'round';
    trailCtx.lineJoin = 'round';
  }

  function buildTrail() {
    if (trailEl && trailEl.isConnected) return;

    trailEl = document.createElement('canvas');
    // "bonj-cursor-exempt" keeps the 3D scene bundle's cursor cleanup away.
    trailEl.className = 'theme-cursor-trail bonj-cursor-exempt is-hidden';
    trailEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(trailEl);
    sizeTrail();
    trailPts = [];
  }

  function readTrailColor() {
    if (!dotEl) return;
    var c = window.getComputedStyle(dotEl).getPropertyValue('--cursor-dot');
    trailColor = (c && c.trim()) || '#fff';
  }

  function measure() {
    if (!dotEl || !ringEl) return;
    dot.halfW = dotEl.offsetWidth / 2 || 6;
    dot.halfH = dotEl.offsetHeight / 2 || 6;
    ring.halfW = ringEl.offsetWidth / 2 || 36;
    ring.halfH = ringEl.offsetHeight / 2 || 36;
  }

  /* ---------------------------------------------------------------
     Node lifecycle
     Shopify re-renders sections (theme editor, cart drawer, Section
     Rendering API) and can swap our nodes out from under us. Re-query
     whenever the ones we hold are no longer in the document.
     --------------------------------------------------------------- */

  function ensureNodes() {
    if (destroyed) return false;

    if (dotEl && dotEl.isConnected && ringEl && ringEl.isConnected) {
      buildTrail();
      return true;
    }

    dotEl = document.querySelector('.theme-cursor--1');
    ringEl = document.querySelector('.theme-cursor--2');
    if (!dotEl || !ringEl) return false;

    buildTrail();
    readTrailColor();
    measure();

    // A fresh node starts unstyled; restore whatever state we were in.
    if (visible && !ignored) show();
    else hide();

    return true;
  }

  /* ---------------------------------------------------------------
     State resolution

     Both hover and ignore are derived from whatever node is under the
     pointer right now, rather than accumulated from over/out pairs.
     That way a missed event can't leave the cursor stuck hidden.
     --------------------------------------------------------------- */

  function resolveState(node) {
    var inIgnore = false;
    var isHover = false;

    if (node && node.closest) {
      inIgnore = !!node.closest(IGNORE_SELECTOR);
      if (!inIgnore) {
        isHover = !!(node.closest(HOVER_SELECTOR) && !node.closest(HOVER_EXCLUDE));
      }
    }

    setIgnored(inIgnore);
    if (!inIgnore) setHover(isHover);
  }

  function onPointerMove(event) {
    if (!ensureNodes()) return;

    pointer.x = event.clientX;
    pointer.y = event.clientY;

    if (!seenPointer) {
      seenPointer = true;
      dot.x = pointer.x;
      dot.y = pointer.y;
      ring.x = pointer.x;
      ring.y = pointer.y;
      snapTrail();
    }

    // Re-derive from the live target. This is what rescues the cursor when
    // it leaves an ignored zone without firing a mouseover on the document.
    if (event.target !== lastTarget) {
      lastTarget = event.target;
      resolveState(event.target);
    }

    if (ignored) return;
    if (!visible) show();
    start();
  }

  function onWindowLeave() {
    hide();
  }

  function onWindowEnter() {
    if (seenPointer && !ignored) show();
  }

  function setHover(state) {
    if (!dotEl || !ringEl) return;
    var target = state ? RING_HOVER_SCALE : 1;
    if (ring.targetScale === target) return;
    ring.targetScale = target;
    dotEl.classList.toggle('is-hover', state);
    ringEl.classList.toggle('is-hover', state);
    start();
  }

  function setIgnored(state) {
    if (ignored === state) return;
    ignored = state;

    if (state) {
      setHover(false);
      hide();
      stop();
      return;
    }

    // Leaving an ignored zone: snap to the current pointer to avoid a fly-in.
    dot.x = pointer.x;
    dot.y = pointer.y;
    ring.x = pointer.x;
    ring.y = pointer.y;
    snapTrail();
    if (seenPointer) {
      show();
      start();
    }
  }

  function onOver(event) {
    if (!ensureNodes()) return;
    lastTarget = event.target;
    resolveState(event.target);
  }

  function onOut(event) {
    // relatedTarget null means the pointer left the window entirely;
    // the documentElement mouseleave handles that case.
    if (!event.relatedTarget) return;
    lastTarget = event.relatedTarget;
    resolveState(event.relatedTarget);
  }

  function snapTrail() {
    trailPts = [];
    if (trailCtx) trailCtx.clearRect(0, 0, window.innerWidth, window.innerHeight);
  }

  function show() {
    if (!dotEl || !ringEl) return;
    visible = true;
    if (trailEl) trailEl.classList.remove('is-hidden');
    dotEl.classList.remove('is-hidden');
    ringEl.classList.remove('is-hidden');
  }

  function hide() {
    visible = false;
    if (trailEl) trailEl.classList.add('is-hidden');
    if (dotEl) dotEl.classList.add('is-hidden');
    if (ringEl) ringEl.classList.add('is-hidden');
  }

  function applyTransforms() {
    if (!dotEl || !ringEl) return;
    dotEl.style.transform =
      'translate3d(' + (dot.x - dot.halfW) + 'px,' + (dot.y - dot.halfH) + 'px,0)';
    ringEl.style.transform =
      'translate3d(' + (ring.x - ring.halfW) + 'px,' + (ring.y - ring.halfH) + 'px,0) scale(' + ring.scale + ')';
  }

  function updateTrail(now) {
    if (!trailCtx) return false;

    // Add a point where the (smoothed) dot currently is.
    var last = trailPts[trailPts.length - 1];
    if (
      visible && !ignored &&
      (!last || Math.hypot(dot.x - last.x, dot.y - last.y) >= TRAIL_MIN_DIST)
    ) {
      // j = fixed per-point wobble so the graphite grain doesn't shimmer
      trailPts.push({ x: dot.x, y: dot.y, t: now, j: Math.random() - 0.5 });
    }

    while (trailPts.length && now - trailPts[0].t > TRAIL_LIFE) trailPts.shift();

    trailCtx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    if (trailPts.length < 2) return trailPts.length > 0;

    trailCtx.strokeStyle = trailColor;

    // Two slightly offset thin passes give the rough, grainy pencil edge.
    for (var pass = 0; pass < 2; pass++) {
      for (var i = 1; i < trailPts.length; i++) {
        var a = trailPts[i - 1];
        var b = trailPts[i];
        var age = (now - b.t) / TRAIL_LIFE;           // 0 new -> 1 gone
        var fade = Math.max(0, 1 - age);
        var off = pass ? 0.7 : -0.5;

        trailCtx.globalAlpha = TRAIL_ALPHA * fade * (pass ? 0.45 : 0.9) * (0.8 + b.j * 0.5);
        trailCtx.lineWidth = TRAIL_WIDTH * (pass ? 0.8 : 1) * (0.55 + 0.45 * fade);
        trailCtx.beginPath();
        trailCtx.moveTo(a.x + off * a.j, a.y + off * 0.6);
        trailCtx.lineTo(b.x + off * b.j, b.y + off * 0.6);
        trailCtx.stroke();
      }
    }

    trailCtx.globalAlpha = 1;
    return true;
  }

  function render() {
    if (!ensureNodes()) {
      stop();
      return;
    }

    dot.x = lerp(dot.x, pointer.x, DOT_EASE);
    dot.y = lerp(dot.y, pointer.y, DOT_EASE);
    ring.x = lerp(ring.x, pointer.x, RING_EASE);
    ring.y = lerp(ring.y, pointer.y, RING_EASE);
    ring.scale = lerp(ring.scale, ring.targetScale, SCALE_EASE);

    applyTransforms();
    var trailMoving = updateTrail(window.performance.now());

    var settled = !trailMoving &&
      Math.abs(dot.x - pointer.x) < EPSILON &&
      Math.abs(dot.y - pointer.y) < EPSILON &&
      Math.abs(ring.x - pointer.x) < EPSILON &&
      Math.abs(ring.y - pointer.y) < EPSILON &&
      Math.abs(ring.scale - ring.targetScale) < EPSILON;

    if (settled) {
      dot.x = pointer.x; dot.y = pointer.y;
      ring.x = pointer.x; ring.y = pointer.y;
      ring.scale = ring.targetScale;
      applyTransforms();
      stop();
      return;
    }

    rafId = window.requestAnimationFrame(render);
  }

  function start() {
    if (running || destroyed) return;
    running = true;
    rafId = window.requestAnimationFrame(render);
  }

  function stop() {
    running = false;
    if (rafId !== null) {
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  function onResize() {
    measure();
    sizeTrail();
  }

  function onSectionChange() {
    // Let Shopify finish swapping the DOM, then re-attach.
    window.requestAnimationFrame(function () {
      ensureNodes();
      measure();
    });
  }

  function onVisibilityChange() {
    if (document.hidden) {
      stop();
    } else if (visible) {
      start();
    }
  }

  function onMotionChange() {
    if (motionQuery.matches) destroy();
  }

  function bind() {
    document.addEventListener('pointermove', onPointerMove, { passive: true, capture: true });
    document.addEventListener('pointerdown', onPointerMove, { passive: true, capture: true });
    document.addEventListener('mouseover', onOver, { passive: true, capture: true });
    document.addEventListener('mouseout', onOut, { passive: true, capture: true });

    // NOT on document with capture: mouseleave/mouseenter do not bubble, but a
    // capturing document listener still receives them for every element on the
    // page, which hid the cursor constantly. On <html> without capture these
    // fire only when the pointer really leaves or re-enters the viewport.
    document.documentElement.addEventListener('mouseleave', onWindowLeave);
    document.documentElement.addEventListener('mouseenter', onWindowEnter);

    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('pageshow', onSectionChange);
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Shopify theme editor / section rendering
    document.addEventListener('shopify:section:load', onSectionChange);
    document.addEventListener('shopify:section:unload', onSectionChange);
    document.addEventListener('shopify:section:reorder', onSectionChange);

    if (motionQuery.addEventListener) motionQuery.addEventListener('change', onMotionChange);
    else if (motionQuery.addListener) motionQuery.addListener(onMotionChange);
  }

  function destroy() {
    destroyed = true;
    stop();

    document.removeEventListener('pointermove', onPointerMove, { capture: true });
    document.removeEventListener('pointerdown', onPointerMove, { capture: true });
    document.removeEventListener('mouseover', onOver, { capture: true });
    document.removeEventListener('mouseout', onOut, { capture: true });
    document.documentElement.removeEventListener('mouseleave', onWindowLeave);
    document.documentElement.removeEventListener('mouseenter', onWindowEnter);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('pageshow', onSectionChange);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    document.removeEventListener('shopify:section:load', onSectionChange);
    document.removeEventListener('shopify:section:unload', onSectionChange);
    document.removeEventListener('shopify:section:reorder', onSectionChange);

    document.documentElement.classList.remove(ROOT_CLASS);
    if (dotEl) dotEl.remove();
    if (ringEl) ringEl.remove();
    if (trailEl) trailEl.remove();
    dotEl = ringEl = trailEl = null;
  }

  function init() {
    dotEl = document.querySelector('.theme-cursor--1');
    ringEl = document.querySelector('.theme-cursor--2');
    if (!dotEl || !ringEl) return;

    hide();
    measure();
    bind();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();